import React, { useEffect, useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ChevronLeft, ChevronRight, Gamepad2, Mic, MicOff, Send, MessageSquare, ZoomIn, ZoomOut, ArrowUpRight, UserPlus, Bot, Clock, X, RotateCcw, Keyboard } from 'lucide-react';
import { EmptyState } from './EmptyState';

/* ── Types ─────────────────────────────────────────── */

type TaskState = 'pending_review' | 'in_review' | 'approved' | 'waiting' | 'completed';
type CardFocus = 'source' | 'response';
type ViewMode = 'review' | 'refine' | 'provide';
type DelegationType = 'self' | 'colleague' | 'agent';
interface ActionItem {
  id: string;
  description: string;
  status: 'resolved' | 'needs_input' | 'delegated';
  inputPrompt?: string;
  userInput?: string;
}
interface Task {
  id: string;
  state: TaskState;
  priority: 'urgent' | 'high' | 'normal' | 'low';
  source: {
    type: 'email' | 'slack' | 'calendar';
    sender: {
      name: string;
      handle: string;
    };
    receivedAt: string;
    rawContent: string;
  };
  summary: string;
  actionItems: ActionItem[];
  draft: {
    body: string;
    canApprove: boolean;
    channel: 'email' | 'slack';
  };
}

/* ── Mock data ─────────────────────────────────────── */

const MOCK_TASKS: Task[] = [{
  id: 'task_01',
  state: 'pending_review',
  priority: 'high',
  source: {
    type: 'email',
    sender: {
      name: 'Paul',
      handle: 'paul@partner.co'
    },
    receivedAt: '10:32 AM',
    rawContent: 'Hi Matt,\n\nCould you send over the privacy policy link? I need it for the client proposal going out tomorrow.\n\nAlso, do you have the updated DPA we discussed last week? And can you confirm the data retention period for the new contract?\n\nThanks,\nPaul'
  },
  summary: 'Paul needs: privacy policy link, updated DPA, and data retention confirmation for client proposal (due tomorrow)',
  actionItems: [{
    id: 'ai_01',
    description: 'Privacy policy link',
    status: 'resolved'
  }, {
    id: 'ai_02',
    description: 'Updated DPA document',
    status: 'needs_input',
    inputPrompt: 'Provide or locate the updated DPA'
  }, {
    id: 'ai_03',
    description: 'Data retention period confirmation',
    status: 'needs_input',
    inputPrompt: 'Confirm the data retention period'
  }],
  draft: {
    body: 'Hi Paul,\n\nHere\'s the privacy policy: https://contextloops.com/privacy\n\nRegarding the DPA — {{ai_02}}\n\nOn data retention — {{ai_03}}\n\nLet me know if you need anything else.\n\nBest,\nMatt',
    canApprove: false,
    channel: 'email'
  }
}, {
  id: 'task_02',
  state: 'pending_review',
  priority: 'normal',
  source: {
    type: 'slack',
    sender: {
      name: 'Marta',
      handle: '@marta'
    },
    receivedAt: '11:15 AM',
    rawContent: 'Hey Matt — just checking if you\'re still free for the design review at 3pm today? I\'ve updated the Figma with the new nav patterns.'
  },
  summary: 'Marta confirming 3pm design review — Figma updated with new nav patterns',
  actionItems: [{
    id: 'ai_04',
    description: 'Confirm attendance at 3pm',
    status: 'resolved'
  }],
  draft: {
    body: 'Yep, 3pm works! I\'ll take a look at the Figma beforehand. See you then.',
    canApprove: true,
    channel: 'slack'
  }
}, {
  id: 'task_03',
  state: 'pending_review',
  priority: 'urgent',
  source: {
    type: 'email',
    sender: {
      name: 'NHS Digital',
      handle: 'integration@nhsd.nhs.uk'
    },
    receivedAt: '9:05 AM',
    rawContent: 'Dear Matt,\n\nAs part of the DTAC compliance review, we require the following by end of week:\n\n1. Updated clinical safety case report (DCB0129)\n2. Evidence of penetration testing within the last 12 months\n3. Confirmation of your Cyber Essentials certification status\n\nPlease submit via the Digital Health Portal.\n\nRegards,\nNHS Digital Compliance Team'
  },
  summary: 'NHS Digital needs: clinical safety report, pen test evidence, and Cyber Essentials status — due end of week',
  actionItems: [{
    id: 'ai_05',
    description: 'Clinical safety case report (DCB0129)',
    status: 'needs_input',
    inputPrompt: 'Locate or upload the DCB0129 report'
  }, {
    id: 'ai_06',
    description: 'Penetration testing evidence',
    status: 'needs_input',
    inputPrompt: 'Provide pen test report or certificate'
  }, {
    id: 'ai_07',
    description: 'Cyber Essentials certification status',
    status: 'resolved'
  }],
  draft: {
    body: 'Dear NHS Digital Compliance Team,\n\nThank you for your email. Please find the requested items below:\n\n1. Clinical safety case report — {{ai_05}}\n2. Penetration testing evidence — {{ai_06}}\n3. Cyber Essentials — We hold current Cyber Essentials certification (certificate attached).\n\nPlease let me know if you require anything further.\n\nBest regards,\nMatt',
    canApprove: false,
    channel: 'email'
  }
}];

/* ── Gamepad hook ──────────────────────────────────── */

interface GamepadState {
  connected: boolean;
  id: string;
  a: boolean;
  b: boolean;
  x: boolean;
  y: boolean;
  l1: boolean;
  r1: boolean;
  l2: number;
  r2: number;
  leftX: number;
  leftY: number;
  rightX: number;
  rightY: number;
  start: boolean;
  select: boolean;
}
const DEADZONE = 0.3;
function useGamepad(): GamepadState {
  const [state, setState] = useState<GamepadState>({
    connected: false,
    id: '',
    a: false,
    b: false,
    x: false,
    y: false,
    l1: false,
    r1: false,
    l2: 0,
    r2: 0,
    leftX: 0,
    leftY: 0,
    rightX: 0,
    rightY: 0,
    start: false,
    select: false
  });
  const prevButtons = useRef<boolean[]>([]);
  useEffect(() => {
    let rafId: number;
    const poll = () => {
      const gps = navigator.getGamepads();
      const gp = gps[0];
      if (gp) {
        const applyDead = (v: number) => Math.abs(v) < DEADZONE ? 0 : v;
        setState({
          connected: true,
          id: gp.id,
          a: gp.buttons[0]?.pressed ?? false,
          b: gp.buttons[1]?.pressed ?? false,
          x: gp.buttons[2]?.pressed ?? false,
          y: gp.buttons[3]?.pressed ?? false,
          l1: gp.buttons[4]?.pressed ?? false,
          r1: gp.buttons[5]?.pressed ?? false,
          l2: gp.buttons[6]?.value ?? 0,
          r2: gp.buttons[7]?.value ?? 0,
          leftX: applyDead(gp.axes[0] ?? 0),
          leftY: applyDead(gp.axes[1] ?? 0),
          rightX: applyDead(gp.axes[2] ?? 0),
          rightY: applyDead(gp.axes[3] ?? 0),
          start: gp.buttons[9]?.pressed ?? false,
          select: gp.buttons[8]?.pressed ?? false
        });
        prevButtons.current = gp.buttons.map(b => b.pressed);
      } else {
        setState(prev => prev.connected ? {
          ...prev,
          connected: false,
          id: ''
        } : prev);
      }
      rafId = requestAnimationFrame(poll);
    };
    rafId = requestAnimationFrame(poll);
    return () => cancelAnimationFrame(rafId);
  }, []);
  return state;
}

/* ── Button-edge hook (fire once per press) ────────── */

function useButtonEdge(pressed: boolean, callback: () => void) {
  const wasPressed = useRef(false);
  useEffect(() => {
    if (pressed && !wasPressed.current) callback();
    wasPressed.current = pressed;
  }, [pressed, callback]);
}

/* ── Gamepad indicator ─────────────────────────────── */

function GamepadIndicator({
  connected,
  id
}: {
  connected: boolean;
  id: string;
}) {
  return <div className="absolute top-4 right-4 flex items-center gap-2 text-xs z-20" data-id="element-2009">
      <Gamepad2 size={14} className={connected ? 'text-[#4a9a9a]' : 'text-[#8a8680]/30'} data-id="element-2010" />
      <span className={connected ? 'text-[#4a9a9a]' : 'text-[#8a8680]/30'} data-id="element-2011">
        {connected ? id.split('(')[0]?.trim().slice(0, 30) || 'Connected' : 'No controller'}
      </span>
    </div>;
}

/* ── Priority badge ────────────────────────────────── */

const PRIORITY_COLORS = {
  urgent: 'bg-red-500/20 text-red-400',
  high: 'bg-amber-500/20 text-amber-400',
  normal: 'bg-[#4a9a9a]/20 text-[#4a9a9a]',
  low: 'bg-[#8a8680]/20 text-[#8a8680]'
};
function PriorityBadge({
  priority
}: {
  priority: Task['priority'];
}) {
  return <span className={`text-[10px] uppercase tracking-[0.1em] font-medium px-2 py-0.5 rounded-full ${PRIORITY_COLORS[priority]}`} data-id="element-2012">
      {priority}
    </span>;
}

/* ── Source icon ───────────────────────────────────── */

const SOURCE_ICONS = {
  email: '✉',
  slack: '⌘',
  calendar: '📅'
};

/* ── Placeholder card ──────────────────────────────── */

function PlaceholderCard({
  item,
  index,
  isActive,
  onProvide,
  onDelegate,
  textScale
}: {
  item: ActionItem;
  index: number;
  isActive: boolean;
  onProvide: (id: string) => void;
  onDelegate: (id: string) => void;
  textScale: number;
}) {
  const borderColor = item.status === 'resolved' ? 'border-[#4a9a9a]/40' : item.status === 'delegated' ? 'border-amber-500/40' : isActive ? 'border-[#e8e4df]/60' : 'border-[#8a8680]/20';
  return <motion.div layout initial={{
    opacity: 0,
    y: 8
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.3,
    delay: index * 0.06
  }} className={`rounded-xl p-4 bg-[#1c1c22] border-l-[3px] ${borderColor} transition-colors`} data-id="element-2013">
      <div className="flex items-start justify-between gap-3" data-id="element-2014">
        <div className="flex-1 min-w-0" data-id="element-2015">
          <div className="flex items-center gap-2 mb-1" data-id="element-2016">
            <span className="text-[10px] uppercase tracking-[0.08em] text-[#8a8680]/50 font-medium" data-id="element-2017">
              {item.status === 'resolved' ? '✓ Resolved' : item.status === 'delegated' ? '⏳ Delegated' : `Action ${index + 1}`}
            </span>
          </div>
          <p className="text-[#e8e4df]/90 leading-relaxed" style={{
          fontSize: `${14 * textScale}px`
        }} data-id="element-2018">
            {item.description}
          </p>
          {item.inputPrompt && item.status === 'needs_input' && <p className="text-[#8a8680]/60 text-xs mt-1" data-id="element-2019">{item.inputPrompt}</p>}
          {item.userInput && <p className="text-[#4a9a9a] text-sm mt-2 italic" data-id="element-2020">"{item.userInput}"</p>}
        </div>
        {item.status === 'needs_input' && isActive && <div className="flex flex-col gap-1.5 shrink-0" data-id="element-2021">
            <button onClick={() => onProvide(item.id)} className="flex items-center gap-1.5 text-[11px] text-[#4a9a9a] hover:text-[#5bb5b5] transition-colors bg-[#4a9a9a]/10 rounded-lg px-2.5 py-1.5" data-id="element-2022">
              <Mic size={11} data-id="element-2023" /> Provide <span className="text-[9px] opacity-50 ml-1" data-id="element-2024">A</span>
            </button>
            <button onClick={() => onDelegate(item.id)} className="flex items-center gap-1.5 text-[11px] text-[#8a8680] hover:text-[#e8e4df] transition-colors bg-[#8a8680]/10 rounded-lg px-2.5 py-1.5" data-id="element-2025">
              <UserPlus size={11} data-id="element-2026" /> Delegate <span className="text-[9px] opacity-50 ml-1" data-id="element-2027">Y</span>
            </button>
          </div>}
        {item.status === 'resolved' && <Check size={14} className="text-[#4a9a9a]/60 shrink-0 mt-1" data-id="element-2028" />}
      </div>
    </motion.div>;
}

/* ── Delegate sheet ────────────────────────────────── */

function DelegateSheet({
  item,
  onClose,
  onConfirm
}: {
  item: ActionItem;
  onClose: () => void;
  onConfirm: (type: DelegationType, instruction: string) => void;
}) {
  const [instruction, setInstruction] = useState('');
  const [delegationType, setDelegationType] = useState<DelegationType>('self');
  const types: {
    type: DelegationType;
    icon: React.ReactNode;
    label: string;
    hint: string;
  }[] = [{
    type: 'self',
    icon: <Clock size={14} data-id="element-2029" />,
    label: 'Do later',
    hint: 'Send to your desktop task list'
  }, {
    type: 'colleague',
    icon: <UserPlus size={14} data-id="element-2030" />,
    label: 'Ask someone',
    hint: 'Draft a message to a colleague'
  }, {
    type: 'agent',
    icon: <Bot size={14} data-id="element-2031" />,
    label: 'Agent search',
    hint: 'ZenBot searches Confluence / Drive'
  }];
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: 20
  }} className="absolute inset-0 z-30 bg-[#141418]/95 backdrop-blur-sm flex items-center justify-center p-6" data-id="element-2032">
      <div className="w-full max-w-md bg-[#1c1c22] rounded-2xl p-6 flex flex-col gap-5" data-id="element-2033">
        <div className="flex items-center justify-between" data-id="element-2034">
          <h3 className="text-[#e8e4df] text-base font-medium" data-id="element-2035">Delegate</h3>
          <button onClick={onClose} className="text-[#8a8680]/50 hover:text-[#e8e4df] transition-colors" data-id="element-2036">
            <X size={16} data-id="element-2037" />
          </button>
        </div>
        <p className="text-[#8a8680] text-sm" data-id="element-2038">{item.description}</p>

        <div className="flex gap-2" data-id="element-2039">
          {types.map(t => <button key={t.type} onClick={() => setDelegationType(t.type)} className={`flex-1 flex flex-col items-center gap-1.5 p-3 rounded-xl border transition-all text-center ${delegationType === t.type ? 'border-[#4a9a9a]/60 bg-[#4a9a9a]/10 text-[#4a9a9a]' : 'border-[#8a8680]/15 text-[#8a8680]/60 hover:border-[#8a8680]/30'}`} data-id="element-2040">
              {t.icon}
              <span className="text-[11px] font-medium" data-id="element-2041">{t.label}</span>
              <span className="text-[9px] opacity-50" data-id="element-2042">{t.hint}</span>
            </button>)}
        </div>

        <div className="relative" data-id="element-2043">
          <textarea value={instruction} onChange={e => setInstruction(e.target.value)} placeholder={delegationType === 'self' ? 'e.g. "Find the PDF when I\'m at my desk"' : delegationType === 'colleague' ? 'e.g. "Ask Paul for the latest metrics"' : 'e.g. "Search Confluence for our refund policy"'} className="w-full bg-[#141418] rounded-xl p-3 pr-10 text-[#e8e4df] text-sm resize-none outline-none min-h-[70px] placeholder:text-[#8a8680]/30" data-id="element-2044" />
          <Mic size={14} className="absolute right-3 top-3 text-[#8a8680]/30" data-id="element-2045" />
        </div>

        <div className="flex justify-end gap-3" data-id="element-2046">
          <button onClick={onClose} className="px-4 py-2 rounded-full text-[#8a8680] text-sm hover:text-[#e8e4df] transition-colors" data-id="element-2047">
            Cancel <span className="text-[9px] opacity-40 ml-1" data-id="element-2048">B</span>
          </button>
          <button onClick={() => onConfirm(delegationType, instruction)} className="px-5 py-2 rounded-full bg-[#4a9a9a] text-[#141418] text-sm font-medium hover:bg-[#5bb5b5] transition-colors" data-id="element-2049">
            Delegate <span className="text-[9px] opacity-60 ml-1" data-id="element-2050">A</span>
          </button>
        </div>
      </div>
    </motion.div>;
}

/* ── Voice input bar ───────────────────────────────── */

function VoiceInputBar({
  listening,
  transcript,
  onToggle,
  onSend,
  onCancel,
  onSwitchToKeyboard
}: {
  listening: boolean;
  transcript: string;
  onToggle: () => void;
  onSend: () => void;
  onCancel: () => void;
  onSwitchToKeyboard: () => void;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: 10
  }} className="flex items-center gap-3 bg-[#1c1c22] rounded-2xl px-4 py-3" data-id="element-2051">
      <button onClick={onToggle} className={`p-2.5 rounded-full transition-all ${listening ? 'bg-red-500/20 text-red-400 animate-pulse' : 'bg-[#4a9a9a]/15 text-[#4a9a9a]'}`} data-id="element-2052">
        {listening ? <MicOff size={16} data-id="element-2053" /> : <Mic size={16} data-id="element-2054" />}
      </button>
      <div className="flex-1 min-w-0" data-id="element-2055">
        {transcript ? <p className="text-[#e8e4df] text-sm truncate" data-id="element-2056">{transcript}</p> : <p className="text-[#8a8680]/40 text-sm" data-id="element-2057">{listening ? 'Listening…' : 'Press A or tap mic to speak'}</p>}
      </div>
      <button onClick={onSwitchToKeyboard} className="text-[#8a8680]/40 hover:text-[#8a8680] transition-colors p-1" data-id="element-2058">
        <Keyboard size={14} data-id="element-2059" />
      </button>
      {transcript && <button onClick={onSend} className="p-2 rounded-full bg-[#4a9a9a] text-[#141418]" data-id="element-2060">
          <Send size={13} data-id="element-2061" />
        </button>}
      <button onClick={onCancel} className="text-[#8a8680]/40 hover:text-[#e8e4df] transition-colors p-1" data-id="element-2062">
        <X size={14} data-id="element-2063" />
      </button>
    </motion.div>;
}

/* ── Main component ────────────────────────────────── */

export function TaskReview() {
  const gamepad = useGamepad();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [focus, setFocus] = useState<CardFocus>('source');
  const [mode, setMode] = useState<ViewMode>('review');
  const [zoomed, setZoomed] = useState(false);
  const [textScale, setTextScale] = useState(1.0);
  const [scrollPos, setScrollPos] = useState(0);
  const [activeItemIndex, setActiveItemIndex] = useState(0);
  const [showDelegate, setShowDelegate] = useState<ActionItem | null>(null);
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [tasks, setTasks] = useState<Task[]>(MOCK_TASKS);
  const sourceRef = useRef<HTMLDivElement>(null);
  const responseRef = useRef<HTMLDivElement>(null);
  const currentTask = tasks[currentIndex];
  const needsInputItems = currentTask?.actionItems.filter(ai => ai.status === 'needs_input') ?? [];

  // ── Navigation ──
  const goNext = useCallback(() => {
    setCurrentIndex(prev => Math.min(prev + 1, tasks.length - 1));
    setFocus('source');
    setMode('review');
    setZoomed(false);
    setActiveItemIndex(0);
    setScrollPos(0);
  }, [tasks.length]);
  const goPrev = useCallback(() => {
    setCurrentIndex(prev => Math.max(prev - 1, 0));
    setFocus('source');
    setMode('review');
    setZoomed(false);
    setActiveItemIndex(0);
    setScrollPos(0);
  }, []);

  // ── Actions ──
  const handleApprove = useCallback(() => {
    if (!currentTask?.draft.canApprove) return;
    setTasks(prev => prev.map((t, i) => i === currentIndex ? {
      ...t,
      state: 'approved'
    } : t));
    setTimeout(goNext, 400);
  }, [currentTask, currentIndex, goNext]);
  const handleRefine = useCallback(() => {
    if (mode === 'refine') {
      setMode('review');
    } else {
      setMode('refine');
      setActiveItemIndex(0);
    }
  }, [mode]);
  const handleProvide = useCallback((itemId: string) => {
    setTranscript('');
    setListening(false);
    // In real app: open voice input targeting this item
    setMode('provide');
  }, []);
  const handleSendTranscript = useCallback(() => {
    if (!transcript || !currentTask) return;
    const item = needsInputItems[activeItemIndex];
    if (!item) return;
    setTasks(prev => prev.map((t, i) => {
      if (i !== currentIndex) return t;
      const updated = {
        ...t,
        actionItems: t.actionItems.map(ai => ai.id === item.id ? {
          ...ai,
          status: 'resolved' as const,
          userInput: transcript
        } : ai)
      };
      const allResolved = updated.actionItems.every(ai => ai.status === 'resolved');
      return {
        ...updated,
        draft: {
          ...updated.draft,
          canApprove: allResolved
        }
      };
    }));
    setTranscript('');
    setListening(false);
    setMode('refine');
  }, [transcript, currentTask, needsInputItems, activeItemIndex, currentIndex]);
  const handleDelegate = useCallback((itemId: string) => {
    const item = currentTask?.actionItems.find(ai => ai.id === itemId);
    if (item) setShowDelegate(item);
  }, [currentTask]);
  const handleDelegateConfirm = useCallback((type: DelegationType, instruction: string) => {
    if (!showDelegate || !currentTask) return;
    setTasks(prev => prev.map((t, i) => {
      if (i !== currentIndex) return t;
      return {
        ...t,
        actionItems: t.actionItems.map(ai => ai.id === showDelegate.id ? {
          ...ai,
          status: 'delegated' as const,
          userInput: `[${type}] ${instruction}`
        } : ai)
      };
    }));
    setShowDelegate(null);
  }, [showDelegate, currentTask, currentIndex]);

  // ── Gamepad: joystick for focus ──
  const joyDebounce = useRef(0);
  useEffect(() => {
    if (!gamepad.connected) return;
    const now = Date.now();
    if (now - joyDebounce.current < 200) return;
    if (gamepad.leftX > 0.5) {
      setFocus('response');
      joyDebounce.current = now;
    }
    if (gamepad.leftX < -0.5) {
      setFocus('source');
      joyDebounce.current = now;
    }
  }, [gamepad.leftX, gamepad.connected]);

  // ── Gamepad: scroll ──
  useEffect(() => {
    if (!gamepad.connected) return;
    const target = focus === 'source' ? sourceRef.current : responseRef.current;
    if (!target || !zoomed) return;
    if (Math.abs(gamepad.rightY) > 0.2) {
      target.scrollTop += gamepad.rightY * 8;
    }
  }, [gamepad.rightY, gamepad.connected, focus, zoomed]);

  // ── Gamepad: refine mode item navigation ──
  const itemDebounce = useRef(0);
  useEffect(() => {
    if (!gamepad.connected || mode !== 'refine') return;
    const now = Date.now();
    if (now - itemDebounce.current < 250) return;
    if (gamepad.leftY > 0.5) {
      setActiveItemIndex(prev => Math.min(prev + 1, needsInputItems.length - 1));
      itemDebounce.current = now;
    }
    if (gamepad.leftY < -0.5) {
      setActiveItemIndex(prev => Math.max(prev - 1, 0));
      itemDebounce.current = now;
    }
  }, [gamepad.leftY, gamepad.connected, mode, needsInputItems.length]);

  // ── Gamepad button edges ──
  useButtonEdge(gamepad.a, useCallback(() => {
    if (showDelegate) return;
    if (mode === 'provide') {
      handleSendTranscript();
      return;
    }
    if (mode === 'refine' && needsInputItems[activeItemIndex]) {
      handleProvide(needsInputItems[activeItemIndex].id);
      return;
    }
    if (mode === 'review' && currentTask?.draft.canApprove) handleApprove();
  }, [mode, showDelegate, needsInputItems, activeItemIndex, currentTask, handleApprove, handleProvide, handleSendTranscript]));
  useButtonEdge(gamepad.b, useCallback(() => {
    if (showDelegate) {
      setShowDelegate(null);
      return;
    }
    if (mode === 'provide') {
      setMode('refine');
      setTranscript('');
      setListening(false);
      return;
    }
    if (mode === 'refine') {
      setMode('review');
      return;
    }
    if (zoomed) {
      setZoomed(false);
      return;
    }
  }, [mode, zoomed, showDelegate]));
  useButtonEdge(gamepad.x, useCallback(() => {
    if (showDelegate) return;
    if (mode === 'review') handleRefine();else if (zoomed) setZoomed(false);else setZoomed(true);
  }, [mode, zoomed, showDelegate, handleRefine]));
  useButtonEdge(gamepad.y, useCallback(() => {
    if (showDelegate) return;
    if (mode === 'refine' && needsInputItems[activeItemIndex]) {
      handleDelegate(needsInputItems[activeItemIndex].id);
    }
  }, [mode, showDelegate, needsInputItems, activeItemIndex, handleDelegate]));
  useButtonEdge(gamepad.l1, useCallback(() => setTextScale(prev => Math.max(0.7, prev - 0.1)), []));
  useButtonEdge(gamepad.r1, useCallback(() => setTextScale(prev => Math.min(1.6, prev + 0.1)), []));
  useButtonEdge(gamepad.l2 > 0.5, useCallback(() => goPrev(), [goPrev]));
  useButtonEdge(gamepad.r2 > 0.5, useCallback(() => goNext(), [goNext]));

  // ── Keyboard fallbacks ──
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === 'ArrowLeft') setFocus('source');
      if (e.key === 'ArrowRight') setFocus('response');
      if (e.key === 'Enter' && mode === 'review' && currentTask?.draft.canApprove) {
        e.preventDefault();
        handleApprove();
      }
      if (e.key === ' ' && mode === 'review') {
        e.preventDefault();
        handleRefine();
      }
      if (e.key === 'Escape') {
        if (mode === 'provide') {
          setMode('refine');
          setTranscript('');
          setListening(false);
        } else if (mode === 'refine') setMode('review');else if (zoomed) setZoomed(false);
      }
      if (e.key === '=' || e.key === '+') setTextScale(prev => Math.min(1.6, prev + 0.1));
      if (e.key === '-') setTextScale(prev => Math.max(0.7, prev - 0.1));
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [mode, zoomed, currentTask, handleApprove, handleRefine]);

  // ── Empty state ──
  if (!currentTask) {
    return <div className="min-h-[calc(100vh-56px)] w-full flex items-center justify-center px-6" data-id="element-2064">
        <EmptyState message="All caught up." data-id="element-2065" />
      </div>;
  }
  const approved = currentTask.state === 'approved';
  return <div className="min-h-[calc(100vh-56px)] w-full flex flex-col relative" data-id="element-2066">
      <GamepadIndicator connected={gamepad.connected} id={gamepad.id} data-id="element-2067" />

      {/* Queue position */}
      <div className="flex items-center justify-between px-6 pt-4 pb-2" data-id="element-2068">
        <div className="flex items-center gap-3" data-id="element-2069">
          <PriorityBadge priority={currentTask.priority} data-id="element-2070" />
          <span className="text-[11px] text-[#8a8680]/40" data-id="element-2071">
            {SOURCE_ICONS[currentTask.source.type]} {currentTask.source.sender.name} · {currentTask.source.receivedAt}
          </span>
        </div>
        <div className="flex items-center gap-2 text-[11px] text-[#8a8680]/40" data-id="element-2072">
          <span data-id="element-2073">{currentIndex + 1} / {tasks.length}</span>
          <div className="flex gap-1" data-id="element-2074">
            <button onClick={goPrev} disabled={currentIndex === 0} className="disabled:opacity-20 hover:text-[#e8e4df] transition-colors" data-id="element-2075"><ChevronLeft size={14} data-id="element-2076" /></button>
            <button onClick={goNext} disabled={currentIndex === tasks.length - 1} className="disabled:opacity-20 hover:text-[#e8e4df] transition-colors" data-id="element-2077"><ChevronRight size={14} data-id="element-2078" /></button>
          </div>
        </div>
      </div>

      {/* ── Two-card layout ── */}
      <div className="flex-1 flex gap-3 px-4 pb-2 overflow-hidden" data-id="element-2079">
        <AnimatePresence mode="wait" data-id="element-2080">
          <motion.div key={`${currentTask.id}-cards`} initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -12
        }} transition={{
          duration: 0.35,
          ease: [0.23, 1, 0.32, 1]
        }} className="flex gap-3 flex-1 min-h-0" data-id="element-2081">
            {/* Source card */}
            <motion.div ref={sourceRef} animate={{
            flex: zoomed && focus === 'source' ? 2 : zoomed && focus === 'response' ? 0.5 : 1
          }} transition={{
            duration: 0.3,
            ease: [0.23, 1, 0.32, 1]
          }} onClick={() => {
            setFocus('source');
            if (focus === 'source') setZoomed(!zoomed);
          }} className={`rounded-2xl bg-[#1c1c22] p-5 overflow-y-auto cursor-pointer transition-all flex flex-col gap-3 ${focus === 'source' ? 'ring-1 ring-[#4a9a9a]/40' : 'ring-1 ring-transparent hover:ring-[#8a8680]/15'}`} data-id="element-2082">
              <div className="flex items-center gap-2 shrink-0" data-id="element-2083">
                <span className="text-[10px] uppercase tracking-[0.1em] text-[#8a8680]/50 font-medium" data-id="element-2084">Incoming</span>
                <span className="text-[10px] text-[#8a8680]/30" data-id="element-2085">{currentTask.draft.channel}</span>
              </div>

              {!zoomed || focus !== 'source' ? <p className="text-[#e8e4df] leading-relaxed" style={{
              fontSize: `${14 * textScale}px`
            }} data-id="element-2086">
                  {currentTask.summary}
                </p> : <div className="flex flex-col gap-3" data-id="element-2087">
                  <p className="text-[#e8e4df] leading-relaxed whitespace-pre-wrap" style={{
                fontSize: `${14 * textScale}px`
              }} data-id="element-2088">
                    {currentTask.source.rawContent}
                  </p>
                  <div className="border-t border-[#8a8680]/10 pt-3 mt-1" data-id="element-2089">
                    <span className="text-[10px] text-[#8a8680]/30" data-id="element-2090">
                      From {currentTask.source.sender.handle}
                    </span>
                  </div>
                </div>}
            </motion.div>

            {/* Response card */}
            <motion.div ref={responseRef} animate={{
            flex: zoomed && focus === 'response' ? 2 : zoomed && focus === 'source' ? 0.5 : 1
          }} transition={{
            duration: 0.3,
            ease: [0.23, 1, 0.32, 1]
          }} onClick={() => {
            setFocus('response');
            if (focus === 'response') setZoomed(!zoomed);
          }} className={`rounded-2xl bg-[#1c1c22] p-5 overflow-y-auto cursor-pointer transition-all flex flex-col gap-3 border-l-[3px] ${currentTask.draft.canApprove ? 'border-l-[#4a9a9a]/50' : 'border-l-amber-500/40'} ${focus === 'response' ? 'ring-1 ring-[#4a9a9a]/40' : 'ring-1 ring-transparent hover:ring-[#8a8680]/15'}`} data-id="element-2091">
              <div className="flex items-center gap-2 shrink-0" data-id="element-2092">
                <span className="text-[10px] uppercase tracking-[0.1em] text-[#8a8680]/50 font-medium" data-id="element-2093">Draft response</span>
                {!currentTask.draft.canApprove && <span className="text-[10px] text-amber-400/60" data-id="element-2094">· {needsInputItems.length} item{needsInputItems.length !== 1 ? 's' : ''} needed</span>}
              </div>
              <div className="text-[#e8e4df]/90 leading-relaxed whitespace-pre-wrap flex-1" style={{
              fontSize: `${14 * textScale}px`
            }} data-id="element-2095">
                {currentTask.draft.body.split(/(\{\{.*?\}\})/g).map((part, i) => {
                const match = part.match(/\{\{(.+?)\}\}/);
                if (match) {
                  const item = currentTask.actionItems.find(ai => ai.id === match[1]);
                  if (item?.userInput) {
                    return <span key={i} className="text-[#4a9a9a] bg-[#4a9a9a]/10 rounded px-1" data-id="element-2096">{item.userInput}</span>;
                  }
                  return <span key={i} className="inline-flex items-center gap-1 text-amber-400/70 bg-amber-500/10 rounded px-1.5 py-0.5 text-[12px]" data-id="element-2097">
                        ⬡ {item?.description || match[1]}
                      </span>;
                }
                return <span key={i} data-id="element-2098">{part}</span>;
              })}
              </div>
            </motion.div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* ── Refine mode: action item cards ── */}
      <AnimatePresence data-id="element-2099">
        {mode === 'refine' && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} className="px-4 pb-2 overflow-hidden" data-id="element-2100">
            <div className="flex gap-2 overflow-x-auto pb-1 pt-1" data-id="element-2101">
              {currentTask.actionItems.map((item, i) => <div key={item.id} className="min-w-[260px] max-w-[320px] flex-shrink-0" data-id="element-2102">
                  <PlaceholderCard item={item} index={i} isActive={mode === 'refine' && i === activeItemIndex + currentTask.actionItems.findIndex(ai => ai.status === 'needs_input')} onProvide={handleProvide} onDelegate={handleDelegate} textScale={textScale} data-id="element-2103" />
                </div>)}
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* ── Voice input bar (provide mode) ── */}
      <AnimatePresence data-id="element-2104">
        {mode === 'provide' && <div className="px-4 pb-2" data-id="element-2105">
            <VoiceInputBar listening={listening} transcript={transcript} onToggle={() => {
          setListening(!listening);
          if (!listening) {
            // Simulate voice capture
            setTimeout(() => setTranscript('The updated DPA is in the shared Drive under Legal > Contracts'), 1500);
          }
        }} onSend={handleSendTranscript} onCancel={() => {
          setMode('refine');
          setTranscript('');
          setListening(false);
        }} onSwitchToKeyboard={() => {
          // In real app: focus a text input
        }} data-id="element-2106" />
          </div>}
      </AnimatePresence>

      {/* ── Bottom actions ── */}
      <div className="px-4 pb-4 pt-1" data-id="element-2107">
        <AnimatePresence mode="wait" data-id="element-2108">
          {approved ? <motion.div key="approved" initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="flex items-center justify-center gap-2 py-3 text-[#4a9a9a]" data-id="element-2109">
              <Check size={16} data-id="element-2110" /> Sent
            </motion.div> : mode === 'review' ? <motion.div key="review-actions" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="flex items-center justify-center gap-3" data-id="element-2111">
              <button onClick={handleApprove} disabled={!currentTask.draft.canApprove} className={`flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-medium transition-all ${currentTask.draft.canApprove ? 'bg-[#4a9a9a] text-[#141418] hover:bg-[#5bb5b5]' : 'bg-[#8a8680]/10 text-[#8a8680]/30 cursor-not-allowed'}`} data-id="element-2112">
                <Check size={14} data-id="element-2113" /> Approve
                <span className="text-[9px] opacity-50 ml-1" data-id="element-2114">A</span>
              </button>
              <button onClick={handleRefine} className="flex items-center gap-2 px-6 py-2.5 rounded-full text-[#8a8680] text-sm font-medium hover:text-[#e8e4df] transition-colors" data-id="element-2115">
                <MessageSquare size={14} data-id="element-2116" /> Refine
                <span className="text-[9px] opacity-40 ml-1" data-id="element-2117">X</span>
              </button>
            </motion.div> : mode === 'refine' ? <motion.div key="refine-actions" initial={{
          opacity: 0,
          y: 6
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0
        }} className="flex items-center justify-center gap-3" data-id="element-2118">
              <button onClick={() => setMode('review')} className="flex items-center gap-2 px-5 py-2.5 rounded-full text-[#8a8680] text-sm hover:text-[#e8e4df] transition-colors" data-id="element-2119">
                <RotateCcw size={13} data-id="element-2120" /> Back
                <span className="text-[9px] opacity-40 ml-1" data-id="element-2121">B</span>
              </button>
              {needsInputItems.length === 0 && <button onClick={handleApprove} className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#4a9a9a] text-[#141418] text-sm font-medium hover:bg-[#5bb5b5] transition-colors" data-id="element-2122">
                  <Check size={14} data-id="element-2123" /> Approve
                  <span className="text-[9px] opacity-50 ml-1" data-id="element-2124">A</span>
                </button>}
            </motion.div> : null}
        </AnimatePresence>

        {/* Button legend */}
        {gamepad.connected && mode === 'review' && <div className="flex items-center justify-center gap-5 mt-3 text-[10px] text-[#8a8680]/25" data-id="element-2125">
            <span data-id="element-2126">🕹 Focus</span>
            <span data-id="element-2127">L1/R1 Text size</span>
            <span data-id="element-2128">L2/R2 Prev/Next</span>
            <span data-id="element-2129">Click Zoom</span>
          </div>}
      </div>

      {/* ── Delegate sheet ── */}
      <AnimatePresence data-id="element-2130">
        {showDelegate && <DelegateSheet item={showDelegate} onClose={() => setShowDelegate(null)} onConfirm={handleDelegateConfirm} data-id="element-2131" />}
      </AnimatePresence>
    </div>;
}